import React, { useState } from 'react';

interface TreatmentPlanTabProps {
  reportData?: any; // We'll properly type this once we know the full structure
}

const TreatmentPlanTab: React.FC<TreatmentPlanTabProps> = ({ reportData }) => {
  const [expandedTreatment, setExpandedTreatment] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Default data in case no report data is provided
  const primaryTreatment = reportData?.analysis_results?.treatment?.primary || {
    drug_name: "Meropenem",
    dosage: "1-2 gm",
    route: "IV",
    frequency: "q8h",
    metadata: {
      adverse_score: 0.36,
      adverse_reactions: [
        { reaction: "Anaemia", count: 17415 },
        { reaction: "Ascites", count: 17415 },
        { reaction: "Blood glucose abnormal", count: 17415 },
        { reaction: "Diabetic metabolic decompensation", count: 17415 },
        { reaction: "Drug clearance decreased", count: 17415 }
      ]
    }
  };

  const alternativeTreatments = reportData?.analysis_results?.treatment?.drug_recommendations?.alternatives || [
    {
      name: "Ertapenem",
      dosage: "1 gm",
      route: "IV/IM",
      frequency: "q24h",
      adverse_score: 0.43,
      adverse_reactions: [
        "Thrombocytopenia",
        "Anaemia",
        "Autoimmune haemolytic anaemia"
      ]
    },
    {
      name: "Imipenem-Cilastatin",
      dosage: "500 mg",
      route: "IV",
      frequency: "q6h", 
      adverse_score: 0.49,
      adverse_reactions: []
    },
    {
      name: "Meropenem-Vaborbactam",
      dosage: "4 gram",
      route: "IV",
      frequency: "q8h",
      adverse_score: 0.13,
      adverse_reactions: []
    }
  ];

  // Filter primary treatment adverse reactions based on search term
  const filteredPrimaryReactions = primaryTreatment?.metadata?.adverse_reactions?.filter(
    (reaction: any) => 
      reaction.reaction.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Toggle expanded treatment
  const toggleTreatment = (index: number) => {
    if (expandedTreatment === index) {
      setExpandedTreatment(null);
    } else {
      setExpandedTreatment(index);
    }
  };

  // Get severity class based on score
  const getSeverityClass = (score: number) => {
    if (score < 0.25) return 'bg-score-none text-score-none';
    if (score < 0.50) return 'bg-score-moderate text-score-moderate';
    return 'bg-score-high text-score-high';
  };

  // Get severity background class
  const getSeverityBgClass = (score: number) => {
    if (score < 0.25) return 'bg-score-none';
    if (score < 0.50) return 'bg-score-moderate';
    return 'bg-score-high';
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="border border-border rounded-md mb-6">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Primary Treatment</h3>
          </div>
          
          <div className="bg-primary bg-opacity-70 p-3 text-white flex justify-between items-center">
            <div className="flex items-center">
              <span className="font-medium">{primaryTreatment.drug_name}</span>
              <span className="ml-3 bg-primary text-white text-xs px-2 py-0.5 rounded">Primary</span>
            </div>
            <div>
              <span className={`${getSeverityBgClass(primaryTreatment.metadata?.adverse_score)} text-white text-xs px-2 py-1 rounded`}>
                Adverse Score: {(primaryTreatment.metadata?.adverse_score * 100).toFixed(1)}%
              </span>
            </div>
          </div>
          
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-y-2">
                  <p><span className="font-medium">Dosage:</span> {primaryTreatment.dosage}</p>
                  <p><span className="font-medium">Route:</span> {primaryTreatment.route}</p>
                  <p><span className="font-medium">Frequency:</span> {primaryTreatment.frequency}</p>
                  <p><span className="font-medium">Duration:</span> 7-10 days</p>
                </div>
              </div>
              
              <h4 className="font-medium mt-4 mb-2">{primaryTreatment.drug_name}</h4>
              <p className="mb-2">
                {primaryTreatment.metadata?.adverse_score < 0.25 
                  ? 'Low Risk' 
                  : primaryTreatment.metadata?.adverse_score < 0.5 
                    ? 'Medium Risk' 
                    : 'High Risk'}
              </p>
              
              <div className="relative h-24 w-full mb-4">
                <div className="absolute inset-0">
                  <svg viewBox="0 0 160 80" className="w-full h-full">
                    {/* Background gradient arc */}
                    <defs>
                      <linearGradient id="gauge-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#8fb9aa" />
                        <stop offset="50%" stopColor="#d16d5e" />
                        <stop offset="100%" stopColor="#b24a3c" />
                      </linearGradient>
                    </defs>
                    <path 
                      d="M 80,80 A 40,40 0 1,1 160,80" 
                      fill="none" 
                      stroke="#e0e0e0" 
                      strokeWidth="12"
                      strokeLinecap="round" 
                    />
                    {/* Calculate the endpoint based on the adverse score */}
                    {primaryTreatment.metadata?.adverse_score && (
                      <path 
                        d={`M 80,80 A 40,40 0 0,1 ${80 + 40 * Math.cos((1 - primaryTreatment.metadata.adverse_score) * Math.PI)},${80 - 40 * Math.sin((1 - primaryTreatment.metadata.adverse_score) * Math.PI)}`}
                        fill="none" 
                        stroke="url(#gauge-gradient)" 
                        strokeWidth="12"
                        strokeLinecap="round" 
                      />
                    )}
                    {primaryTreatment.metadata?.adverse_score && (
                      <circle 
                        cx={80 + 40 * Math.cos((1 - primaryTreatment.metadata.adverse_score) * Math.PI)} 
                        cy={80 - 40 * Math.sin((1 - primaryTreatment.metadata.adverse_score) * Math.PI)} 
                        r="6" 
                        fill="#4a4a4a" 
                      />
                    )}
                    <text x="80" y="70" textAnchor="middle" fontSize="16" fontWeight="bold">
                      {(primaryTreatment.metadata?.adverse_score * 100).toFixed(0)}%
                    </text>
                  </svg>
                </div>
              </div>
              
              <h4 className="font-medium mt-2 mb-2">Common Adverse Reactions:</h4>
              <ul className="list-disc ml-5">
                {primaryTreatment.metadata?.adverse_reactions?.slice(0, 3).map((reaction: any, index: number) => (
                  <li key={index}>{reaction.reaction}</li>
                ))}
              </ul>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium">Adverse Reactions</h4>
                <div className="text-primary">
                  <span className="text-xs mr-1">Total: {primaryTreatment.metadata?.adverse_reactions?.length || 0}</span>
                </div>
              </div>
              
              <div className="relative mb-3">
                <input
                  type="text"
                  placeholder="Search reactions"
                  className="w-full border border-gray-300 rounded-md py-2 pl-8 pr-4 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 font-medium">Reaction <span className="text-red-500">*</span></th>
                      <th className="text-left py-2 font-medium">Severity</th>
                      <th className="text-left py-2 font-medium">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPrimaryReactions.slice(0, 10).map((reaction: any, index: number) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="py-2">{reaction.reaction}</td>
                        <td className="py-2">
                          <span className="bg-score-none bg-opacity-20 text-score-none text-xs px-2 py-0.5 rounded">
                            {reaction.outcome || 'Low'}
                          </span>
                        </td>
                        <td className="py-2">{reaction.count ? (reaction.count > 10000 ? Math.floor(reaction.count / 1000) + 'K' : reaction.count) : 1}</td>
                      </tr>
                    ))}
                    {filteredPrimaryReactions.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-4 text-center text-gray-500">
                          No adverse reactions found matching "{searchTerm}"
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="flex items-center justify-between mt-3">
                <div className="flex space-x-2">
                  <span className="bg-score-none bg-opacity-20 text-score-none text-xs px-2 py-0.5 rounded">
                    Low: {primaryTreatment.metadata?.adverse_reactions?.filter((r: any) => r.outcome === 'Low' || r.outcome === 'Unknown').length || 0}
                  </span>
                  <span className="bg-score-moderate bg-opacity-20 text-score-moderate text-xs px-2 py-0.5 rounded">
                    Medium: {primaryTreatment.metadata?.adverse_reactions?.filter((r: any) => r.outcome === 'Medium').length || 0}
                  </span>
                  <span className="bg-score-high bg-opacity-20 text-score-high text-xs px-2 py-0.5 rounded">
                    High: {primaryTreatment.metadata?.adverse_reactions?.filter((r: any) => r.outcome === 'High').length || 0}
                  </span>
                </div>
                {filteredPrimaryReactions.length > 10 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs">1</span>
                    <span className="px-2 py-0.5 bg-primary text-white text-xs rounded">2</span>
                    <span className="text-xs">of {Math.ceil(filteredPrimaryReactions.length / 10)}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="border border-border rounded-md mb-6">
          <div className="bg-section-light p-4">
            <h3 className="text-lg font-semibold text-text">Alternative Treatments</h3>
          </div>
          
          <div className="space-y-4 p-4">
            {alternativeTreatments.map((treatment: any, index: number) => (
              <div key={index} className="bg-gray-100 rounded-md overflow-hidden shadow-sm">
                <div 
                  className={`p-3 text-white flex justify-between items-center cursor-pointer ${expandedTreatment === index ? 'bg-gray-700' : 'bg-gray-600'}`}
                  onClick={() => toggleTreatment(index)}
                >
                  <div className="flex items-center">
                    <span className="font-medium">{treatment.name}</span>
                    <div className="ml-2">
                      {expandedTreatment === index ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className={`${getSeverityBgClass(treatment.adverse_score)} text-white text-xs px-2 py-1 rounded`}>
                    Adverse Score: {(treatment.adverse_score * 100).toFixed(1)}%
                  </span>
                </div>
                
                {expandedTreatment === index && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <div className="grid grid-cols-2 gap-y-2 mb-4">
                          <p><span className="font-medium">Dosage:</span> {treatment.dosage}</p>
                          <p><span className="font-medium">Route:</span> {treatment.route}</p>
                          <p><span className="font-medium">Frequency:</span> {treatment.frequency}</p>
                          <p><span className="font-medium">Duration:</span> 7-10 days</p>
                        </div>
                        
                        <div className="relative h-24 w-full mb-4">
                          <div className="absolute inset-0">
                            <svg viewBox="0 0 160 80" className="w-full h-full">
                              <path 
                                d="M 80,80 A 40,40 0 1,1 160,80" 
                                fill="none" 
                                stroke="#e0e0e0" 
                                strokeWidth="12"
                                strokeLinecap="round" 
                              />
                              <path 
                                d={`M 80,80 A 40,40 0 0,1 ${80 + 40 * Math.cos((1 - treatment.adverse_score) * Math.PI)},${80 - 40 * Math.sin((1 - treatment.adverse_score) * Math.PI)}`}
                                fill="none" 
                                stroke="url(#gauge-gradient)" 
                                strokeWidth="12"
                                strokeLinecap="round" 
                              />
                              <circle 
                                cx={80 + 40 * Math.cos((1 - treatment.adverse_score) * Math.PI)} 
                                cy={80 - 40 * Math.sin((1 - treatment.adverse_score) * Math.PI)} 
                                r="6" 
                                fill="#4a4a4a" 
                              />
                              <text x="80" y="70" textAnchor="middle" fontSize="16" fontWeight="bold">
                                {(treatment.adverse_score * 100).toFixed(0)}%
                              </text>
                            </svg>
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          {treatment.special_notes && (
                            <div className="mb-4">
                              <h4 className="font-medium mb-1">Special Notes:</h4>
                              <p className="text-sm">{treatment.special_notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium mb-4">Adverse Reactions:</h4>
                        {treatment.adverse_reactions && treatment.adverse_reactions.length > 0 ? (
                          <div className="overflow-y-auto max-h-60">
                            <ul className="list-disc pl-5 space-y-1">
                              {treatment.adverse_reactions.map((reaction: string | any, idx: number) => (
                                <li key={idx} className="text-sm">
                                  {typeof reaction === 'string' ? reaction : reaction.reaction}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No detailed adverse reactions data available</p>
                        )}
                        
                        <div className="flex items-center space-x-2 mt-4">
                          <span className={`${getSeverityClass(treatment.adverse_score)} bg-opacity-20 text-xs px-2 py-0.5 rounded`}>
                            {treatment.adverse_score < 0.25 
                              ? 'Low Risk' 
                              : treatment.adverse_score < 0.5 
                                ? 'Medium Risk' 
                                : 'High Risk'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {treatment.adverse_reactions ? `${treatment.adverse_reactions.length} reactions` : 'No data'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {expandedTreatment !== index && (
                  <div className="p-3 grid grid-cols-4 gap-2 bg-white">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      <span className="text-sm">{treatment.dosage}</span>
                    </div>
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a2.5 2.5 0 015 0v6m-5 0h5m-5 0v6a2.5 2.5 0 005 0v-6m5 0v-2m0 2v6a2.5 2.5 0 01-5 0v-6m5 0h-5" />
                      </svg>
                      <span className="text-sm">{treatment.route}</span>
                    </div>
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">{treatment.frequency}</span>
                    </div>
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">7-10 days</span>
                    </div>
                  </div>
                )}
                
                {expandedTreatment === index && treatment.adverse_reactions && (
                  <div className="px-4 pb-4 bg-white flex items-center space-x-2">
                    <span className="bg-score-high bg-opacity-20 text-score-high text-xs px-2 py-0.5 rounded">
                      {treatment.adverse_reactions.filter((r: any) => 
                        (typeof r === 'object' && r.outcome === 'High') || 
                        (typeof r === 'string' && r === 'High')
                      ).length || 0} High
                    </span>
                    <span className="bg-score-moderate bg-opacity-20 text-score-moderate text-xs px-2 py-0.5 rounded">
                      {treatment.adverse_reactions.filter((r: any) => 
                        (typeof r === 'object' && r.outcome === 'Medium') || 
                        (typeof r === 'string' && r === 'Medium')
                      ).length || 0} Med
                    </span>
                    <span className="bg-score-none bg-opacity-20 text-score-none text-xs px-2 py-0.5 rounded">
                      {treatment.adverse_reactions.filter((r: any) => 
                        (typeof r === 'object' ? (r.outcome === 'Low' || r.outcome === 'Unknown') : true) || 
                        (typeof r === 'string' && r === 'Low')
                      ).length || 0} Low
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="p-4">
            <h3 className="text-lg font-semibold text-text mb-4">Treatment Comparison</h3>
            <p className="text-sm mb-6">Click on any treatment bar to view detailed adverse reactions</p>
            
            <div className="relative h-64 w-full">
              <div className="absolute left-0 bottom-0 w-full h-full flex items-end">
                <div className="flex items-end justify-around w-full h-full">
                  {/* Visualization for treatment comparison */}
                  {[primaryTreatment, ...alternativeTreatments.slice(0, 3)].map((treatment: any, index: number) => (
                    <div 
                      key={index} 
                      className="flex flex-col items-center space-y-2 cursor-pointer"
                      onClick={() => index === 0 ? null : toggleTreatment(index-1)}
                    >
                      <div className="flex flex-col items-center">
                        <div 
                          className="h-40 w-16 bg-blue-200 rounded-t-md"
                          style={{ opacity: 1 - (treatment.metadata?.adverse_score || treatment.adverse_score) }}
                        ></div>
                        <div 
                          className={`w-16 rounded-b-md ${(treatment.metadata?.adverse_score || treatment.adverse_score) < 0.25 ? 'bg-green-300' : (treatment.metadata?.adverse_score || treatment.adverse_score) < 0.5 ? 'bg-yellow-300' : 'bg-red-300'}`}
                          style={{ 
                            height: `${((treatment.metadata?.adverse_score || treatment.adverse_score) * 100) / 2}px`,
                            transition: 'height 0.3s ease-in-out'
                          }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium">{treatment.drug_name || treatment.name}</span>
                      <span className="text-xs">{((treatment.metadata?.adverse_score || treatment.adverse_score) * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute left-0 bottom-0 w-full">
                <div className="border-t border-gray-300 w-full"></div>
              </div>
            </div>
            
            <div className="flex justify-center space-x-4 mt-4">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-200 mr-2"></div>
                <span className="text-xs">Efficacy (%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-300 mr-2"></div>
                <span className="text-xs">Adverse Score (%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanTab; 